
import { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import PropertyMainImage from './PropertyMainImage';
import PropertyImageThumbnail from './PropertyImageThumbnail';

interface PropertyImageFullscreenViewProps {
  images: string[];
  title: string;
  initialSlide: number;
  onClose: () => void;
}

const PropertyImageFullscreenView = ({
  images,
  title,
  initialSlide,
  onClose
}: PropertyImageFullscreenViewProps) => {
  const [currentSlide, setCurrentSlide] = useState(initialSlide);
  
  // Reset current slide when initial slide changes
  useEffect(() => {
    setCurrentSlide(initialSlide);
  }, [initialSlide]);

  const goToNextSlide = () => {
    setCurrentSlide((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };
  
  const goToPrevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleThumbnailClick = (index: number) => {
    setCurrentSlide(index);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      {/* Close button */}
      <button 
        onClick={onClose}
        className="absolute top-4 right-4 z-10 bg-black/50 hover:bg-black/70 rounded-full p-2 text-white"
        aria-label="Close fullscreen view"
      >
        <X size={24} />
      </button>
      
      {/* Main image container */}
      <div className="flex-1 relative flex items-center justify-center">
        <PropertyMainImage 
          image={images[currentSlide]} 
          title={title} 
          index={currentSlide}
        />
        
        {/* Navigation arrows - updated with larger, more visible buttons */}
        <button
          onClick={goToPrevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 transition-colors rounded-full p-4 text-white z-10"
          aria-label="Previous image"
        >
          <ChevronLeft className="h-8 w-8" />
        </button>
        
        <button
          onClick={goToNextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 transition-colors rounded-full p-4 text-white z-10"
          aria-label="Next image"
        >
          <ChevronRight className="h-8 w-8" />
        </button>

        {/* Image counter */}
        <div className="absolute top-4 left-4 bg-black/70 backdrop-blur-sm px-4 py-1 rounded-full text-white">
          {currentSlide + 1} / {images.length}
        </div>
      </div>
      
      {/* Thumbnails at the bottom */}
      {images.length > 1 && (
        <div className="h-24 bg-black/90 flex gap-2 p-2 overflow-x-auto">
          {images.map((image, idx) => (
            <PropertyImageThumbnail
              key={idx}
              image={image}
              index={idx}
              currentSlide={currentSlide}
              onClick={handleThumbnailClick}
              title={title}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default PropertyImageFullscreenView;
